import {
  addWeeks,
  addDays,
  addMonths,
  addYears,
  isBefore,
  parseISO,
  startOfDay,
  format,
  isValid,
  isAfter,
  isSameMonth,
  startOfMonth,
  endOfMonth,
  isSameDay,
  formatDistanceToNow,
  differenceInDays
} from 'date-fns';

/**
 * Robustly parses a date string or object into a Date object
 */
export const toDate = (date) => {
  if (!date) return null;
  if (date instanceof Date) return isValid(date) ? date : null;
  try {
    let parsed = parseISO(date);
    if (isValid(parsed)) return parsed;
    parsed = new Date(date);
    return isValid(parsed) ? parsed : null;
  } catch (e) {
    return null;
  }
};

/**
 * Calculates total occurrences of a billing cycle from commencement to now (or cancellation)
 */
export const calculateTotalOccurrences = (commencement, interval, cancelledAt = null) => {
  const start = toDate(commencement);
  if (!start) return 0;
  
  const end = cancelledAt ? toDate(cancelledAt) : startOfDay(new Date());
  if (isBefore(end, start)) return 0;

  let count = 0;
  let current = startOfDay(start);
  const target = startOfDay(end);

  // Safety break to prevent infinite loops
  let safety = 0;
  while ((isBefore(current, target) || isSameDay(current, target)) && safety < 5000) {
    count++;
    switch (interval) {
      case 'Weekly': current = addWeeks(current, 1); break;
      case 'Fortnightly': current = addWeeks(current, 2); break;
      case '28 Days': current = addDays(current, 28); break;
      case 'Monthly': current = addMonths(current, 1); break;
      case 'Annual': current = addYears(current, 1); break;
      case 'Every Two Years': current = addYears(current, 2); break;
      default: current = addMonths(current, 1);
    }
    safety++;
  }
  return count;
};

/**
 * Calculates the next occurrence of the 08:00 UTC cron job
 */
export const getNextCronExecution = (frequency, lastSent) => {
  const now = new Date();
  const lastSentDate = toDate(lastSent);

  let nextTrigger = new Date(Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
    8, 0, 0
  ));

  if (now > nextTrigger) {
    nextTrigger.setUTCDate(nextTrigger.getUTCDate() + 1);
  }

  if (lastSentDate) {
    let earliestPossible;
    if (frequency === 'Daily') earliestPossible = addDays(lastSentDate, 1);
    else if (frequency === 'Weekly') earliestPossible = addWeeks(lastSentDate, 1);
    else if (frequency === 'Monthly') earliestPossible = addMonths(lastSentDate, 1);
    else earliestPossible = addWeeks(lastSentDate, 1);

    if (nextTrigger < earliestPossible) {
      nextTrigger = new Date(Date.UTC(
        earliestPossible.getUTCFullYear(),
        earliestPossible.getUTCMonth(),
        earliestPossible.getUTCDate(),
        8, 0, 0
      ));
      if (nextTrigger < earliestPossible) {
        nextTrigger.setUTCDate(nextTrigger.getUTCDate() + 1);
      }
    }
  }

  return {
    date: nextTrigger,
    relative: formatDistanceToNow(nextTrigger, { addSuffix: true }),
    localTime: format(nextTrigger, 'PPp')
  };
};

export const getOccurrencesInMonth = (monthDate, commencement, interval, cancelledAt = null) => {
  const startDate = toDate(commencement);
  const cancelDate = cancelledAt ? toDate(cancelledAt) : null;
  if (!startDate) return [];

  const monthStart = startOfMonth(monthDate);
  const monthEnd = endOfMonth(monthDate);
  const occurrences = [];

  let current = startOfDay(startDate);
  const maxLookahead = addYears(monthEnd, 1);

  while (isBefore(current, maxLookahead)) {
    if (cancelDate && isAfter(current, cancelDate)) break;
    if (isSameMonth(current, monthDate)) {
      occurrences.push(new Date(current));
    }
    if (isAfter(current, monthEnd)) break;

    switch (interval) {
      case 'Weekly': current = addWeeks(current, 1); break;
      case 'Fortnightly': current = addWeeks(current, 2); break;
      case '28 Days': current = addDays(current, 28); break;
      case 'Monthly': current = addMonths(current, 1); break;
      case 'Annual': current = addYears(current, 1); break;
      case 'Every Two Years': current = addYears(current, 2); break;
      default: current = addMonths(current, 1);
    }
  }
  return occurrences;
};

export const getNextDueDate = (commencement, interval) => {
  const startDate = toDate(commencement);
  if (!startDate) return null;

  let date = startOfDay(startDate);
  const today = startOfDay(new Date());

  if (!isBefore(date, today)) return date;

  let safetyLoop = 0;
  while (isBefore(date, today) && safetyLoop < 2000) {
    switch (interval) {
      case 'Weekly': date = addWeeks(date, 1); break;
      case 'Fortnightly': date = addWeeks(date, 2); break;
      case '28 Days': date = addDays(date, 28); break;
      case 'Monthly': date = addMonths(date, 1); break;
      case 'Annual': date = addYears(date, 1); break;
      case 'Every Two Years': date = addYears(date, 2); break;
      default: date = addMonths(date, 1);
    }
    safetyLoop++;
  }
  return date;
};

export const formatDate = (date) => {
  const d = toDate(date);
  if (!d) return 'N/A';
  return format(d, 'MMM d, yyyy');
};

export const getNormalizedMonthlyCost = (amount, interval) => {
  const val = parseFloat(amount) || 0;
  switch (interval) {
    case 'Weekly': return val * (52 / 12);
    case 'Fortnightly': return val * (26 / 12);
    case '28 Days': return val * (365 / 28 / 12);
    case 'Monthly': return val;
    case 'Annual': return val / 12;
    case 'Every Two Years': return val / 24;
    default: return val;
  }
};